/**
 * gocart-patch.js  v6
 * ─────────────────────────────────────────────────────────────────────────────
 * Root causes fixed:
 *
 *  A. GoCart's +/– click handler reads the qty input value WITHOUT updating
 *     it first.  Rapid clicks all read the same stale number, so a standard
 *     debounce that just uses the last argument only ever adds/removes 1.
 *     Fix: collect all calls in a burst, detect the stale-DOM pattern
 *     (all args identical), look up the real server qty from this.cart_obj,
 *     and compute finalQty = serverQty + (delta × clickCount).
 *
 *  B. BOGO / bundle logic fires multiple /cart/change.js POSTs concurrently
 *     → 409 Conflict.
 *     Fix: sequential queue — all cart mutations run one at a time.
 *
 *  C. Cloudflare returns 429 HTML challenge when it sees a burst of /cart.js
 *     GETs (GoCart calls fetchCart after every mutation).
 *     Fix: debounce fetchCart 300 ms + 2-retry back-off on 429/5xx.
 *
 *  D. "Something went wrong" modal fires on any error including transient
 *     ones that are already being retried.
 *     Fix: suppress ajaxRequestFail entirely; log to console only.
 * ─────────────────────────────────────────────────────────────────────────────
 */

(function () {
  "use strict";

  /* ─── 1. CAPTURE NATIVE FETCH ───────────────────────────────────────────── */
  var _nativeFetch = window.fetch.bind(window);

  /* ─── 2. DEBOUNCE UTILITY ───────────────────────────────────────────────── */
  function debounce(fn, wait) {
    var timer = null;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  /* ─── 3. RETRY-CAPABLE FETCH ────────────────────────────────────────────── */
  function cartFetch(url, options, retries, delay) {
    retries = retries !== undefined ? retries : 2;
    delay   = delay   !== undefined ? delay   : 2000;

    return _nativeFetch(url, options)
      .then(function (response) {
        if ((response.status === 429 || response.status >= 500) && retries > 0) {
          console.warn("[gocart-patch] " + response.status + " on " + url +
            " — retrying in " + delay + "ms (" + retries + " left)");
          return new Promise(function (resolve) {
            setTimeout(function () {
              resolve(cartFetch(url, options, retries - 1, Math.min(delay * 2, 8000)));
            }, delay);
          });
        }
        return response;
      })
      .catch(function (err) {
        if (retries > 0) {
          return new Promise(function (resolve) {
            setTimeout(function () {
              resolve(cartFetch(url, options, retries - 1, Math.min(delay * 2, 8000)));
            }, delay);
          });
        }
        throw err;
      });
  }

  /* ─── 4. SEQUENTIAL MUTATION QUEUE ─────────────────────────────────────── */
  var _queue   = [];
  var _running = false;

  function _processQueue() {
    if (_running || _queue.length === 0) return;
    _running = true;
    var item = _queue.shift();
    cartFetch(item.url, item.options)
      .then(function (res) { item.resolve(res); _running = false; _processQueue(); })
      .catch(function (err) { item.reject(err);  _running = false; _processQueue(); });
  }

  function enqueue(url, options) {
    return new Promise(function (resolve, reject) {
      _queue.push({ url: url, options: options, resolve: resolve, reject: reject });
      _processQueue();
    });
  }

  /* ─── 5. SMART window.fetch REPLACEMENT ────────────────────────────────── */
  window.fetch = function smartFetch(url, options) {
    if (typeof url === "string" && /^\/cart/.test(url)) {
      var method = ((options && options.method) || "GET").toUpperCase();
      return method !== "GET" ? enqueue(url, options) : cartFetch(url, options);
    }
    return _nativeFetch.apply(window, arguments);
  };

  /* ─── 6. PATCH GoCart PROTOTYPE ────────────────────────────────────────── */

  function applyPatches() {
    if (typeof window.GoCart === "undefined") {
      setTimeout(applyPatches, 50);
      return;
    }

    var proto = window.GoCart.prototype;

    /* ── 6a. SMART qty accumulator ────────────────────────────────────────
     *
     * GoCart's +/– click handler (gocart.js ~line 3597):
     *   var a = Number(input.value) + 1;   // reads input
     *   e.changeItemQuantity(r, a);        // calls with stale value
     * It does NOT update input.value before calling, so rapid clicks all
     * pass the same argument (e.g. qty=6 when server has 5).
     *
     * We collect every call in a burst window (400 ms), then:
     *  • If all args are identical → stale-DOM burst → look up server qty
     *    from this.cart_obj.items and compute:
     *      finalQty = serverQty + (clickDelta × numberOfClicks)
     *  • If args are different → GoCart is updating the DOM → use last value.
     */
    var _origChangeQty = proto.changeItemQuantity;
    var _qtyBursts = {}; // line → { qtys: [], timer }

    proto.changeItemQuantity = function (line, qty) {
      var self = this;

      /* Disable checkout IMMEDIATELY on first click — before the debounce
       * delay, before the request fires, before anything.  GoCart's own
       * fetchCart success callback (.go-cart__checkout_btn disabled=false)
       * will re-enable it once the cart has been updated server-side.      */
      jQuery(".go-cart__checkout_btn").attr("disabled", true);

      if (!_qtyBursts[line]) {
        _qtyBursts[line] = { qtys: [], timer: null };
      }
      _qtyBursts[line].qtys.push(qty);

      clearTimeout(_qtyBursts[line].timer);
      _qtyBursts[line].timer = setTimeout(function () {
        var burst = _qtyBursts[line];
        delete _qtyBursts[line];

        var qtys    = burst.qtys;
        var lastQty = qtys[qtys.length - 1];
        var finalQty;

        if (qtys.length === 1) {
          /* Single click — straightforward */
          finalQty = lastQty;

        } else {
          var allSame = qtys.every(function (q) { return q === qtys[0]; });

          if (allSame) {
            /* ── Stale-DOM pattern ──
             * All clicks sent the same number (e.g. always 6 when cart has 5).
             * Use GoCart's live cart_obj to find the real server qty, then
             * multiply the per-click delta by the total click count.
             */
            var serverQty = null;
            if (self.cart_obj && Array.isArray(self.cart_obj.items)) {
              var idx  = parseInt(line, 10) - 1; // line is 1-based
              var item = self.cart_obj.items[idx];
              if (item) serverQty = item.quantity;
            }

            if (serverQty !== null) {
              var perClickDelta = qtys[0] - serverQty; // +1 for plus, -1 for minus
              finalQty = serverQty + (perClickDelta * qtys.length);
              console.log(
                "[gocart-patch] Stale-DOM burst detected on line " + line +
                ": " + qtys.length + " clicks × (" + (perClickDelta > 0 ? "+" : "") +
                perClickDelta + ") from serverQty=" + serverQty +
                " → finalQty=" + finalQty
              );
            } else {
              /* cart_obj not available yet — fall back to last arg */
              finalQty = lastQty;
            }

          } else {
            /* ── GoCart is updating the DOM between clicks ──
             * Args are incrementing (6, 7, 8 …).  The last value is the
             * true intended final quantity — standard debounce behaviour.
             */
            finalQty = lastQty;
          }
        }

        finalQty = Math.max(0, finalQty); // never go negative
        _origChangeQty.call(self, line, finalQty);

      }, 400); // 400 ms silence = burst is done
    };

    /* ── 6b. Debounce fetchCart (300 ms) ──────────────────────────────────
     * Collapses the cascade of fetchCart() calls that GoCart fires after
     * every mutation into ONE /cart.js GET once everything has settled.
     */
    var _origFetchCart = proto.fetchCart;
    var _debouncedFetchCart = debounce(function (callback) {
      _origFetchCart.call(this, callback);
    }, 300);
    proto.fetchCart = function (callback) {
      _debouncedFetchCart.call(this, callback);
    };

    /* ── 6c. Suppress "Something went wrong" modal ───────────────────────*/
    proto.ajaxRequestFail = function () {
      console.warn("[gocart-patch] Cart request failed — modal suppressed. " +
        "Check Network tab for details.");
    };

    /* ── 6d. B2B bulk add-to-cart ────────────────────────────────────────*/
    document.addEventListener("click", function (ev) {
      if (!ev.target.matches(".js-go-cart-add-to-cart-b2b")) return;
      ev.stopImmediatePropagation();
      ev.preventDefault();

      var btn       = ev.target;
      var variantId = parseInt(btn.dataset.variantId, 10);
      var row       = btn.closest(".b2b-eot__row");
      var qtyInput  = row ? row.querySelector(".b2b-eot__qty-input") : null;
      var qty       = qtyInput ? parseInt(qtyInput.value, 10) : 0;
      if (!variantId || qty <= 0) return;

      btn.disabled = true;
      enqueue("/cart/add.js", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: variantId, quantity: qty }),
      })
        .then(function (res) { return res.json(); })
        .then(function () {
          btn.disabled = false;
          var gc = window.goCart || window.cart_instance || null;
          if (gc && typeof gc.fetchAndOpenCart === "function") gc.fetchAndOpenCart();
        })
        .catch(function (err) {
          btn.disabled = false;
          console.warn("[gocart-patch] B2B add-to-cart failed:", err);
        });
    }, true);

    console.log("[gocart-patch] v6 ✅ Smart qty accumulator + queue + retry active.");
  }

  /* ─── 7. BOOTSTRAP ──────────────────────────────────────────────────────── */
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", applyPatches);
  } else {
    applyPatches();
  }
})();
